/**
 * Frontend privacy guardrails for public creator profile text.
 *
 * This is a defence-in-depth presentation layer, not the security boundary.
 * Restricted contact data must still be removed or access-controlled by the API.
 */

export const CONTACT_REDACTION_LABEL = "[Business contact hidden]";
const EMAIL_PATTERN =
  /(?:mailto:\s*)?[a-z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)+/gi;

const WHATSAPP_PATTERN =
  /(?:https?:\/\/)?(?:www\.)?(?:wa\.me\/[^\s<]+|api\.whatsapp\.com\/send[^\s<]*|whatsapp:\/\/send[^\s<]*)/gi;

// Conservative phone matching: only values beginning with +, 00 or 0 and
// containing at least ten digits are treated as phone numbers. This avoids
// hiding ordinary follower counts, dates, scores and creator IDs.
const PHONE_CANDIDATE_PATTERN = /(?:\+|00|0)\d[\d\s().-]{7,}\d/g;

const SENSITIVE_CONTACT_KEY_PATTERN =
  /(?:e_?mail|mail_?address|phone|mobile|telephone|whatsapp|street_?address|postal_?address|home_?address|contact_?(?:email|phone|number|address))/i;

const HUMAN_TEXT_KEY_PATTERN =
  /(?:bio|biography|description|caption|about|summary|reason|note|text|message)/i;

const URL_KEY_PATTERN = /(?:url|uri|website|link|href)/i;
const RESTRICTED_CONTACT_URL_PATTERN =
  /^(?:mailto:|tel:|sms:|whatsapp:)|(?:^|\/\/)(?:www\.)?(?:wa\.me|api\.whatsapp\.com)(?:\/|$)/i;

const emailOnlySanitize = (value) =>
  String(value ?? "")
    .replace(EMAIL_PATTERN, CONTACT_REDACTION_LABEL)
    .replace(WHATSAPP_PATTERN, CONTACT_REDACTION_LABEL);

const replacePhoneCandidates = (value) =>
  String(value ?? "").replace(PHONE_CANDIDATE_PATTERN, (candidate) => {
    const digitCount = candidate.replace(/\D/g, "").length;
    return digitCount >= 10 && digitCount <= 16
      ? CONTACT_REDACTION_LABEL
      : candidate;
  });

const CONTACT_CUE_PATTERN =
  "(?:business(?:\\s+enquiries?)?|collabs?|collaborations?|contact|e-?mail|phone|mobile|telephone|whats\\s*app|bookings?|work\\s+with\\s+me)";
const CONTACT_EMOJI_PATTERN = "(?:📧|✉️?|💌|📩|☎️?|📞|📱|📲)";
const CONTACT_REDACTION_ARTIFACT_SOURCE =
  "\\[\\s*(?:(?:business|direct)\\s+)?(?:contact|e-?mail|phone|mobile|telephone|whats\\s*app)(?:\\s+(?:details?|address|number))?\\s+(?:hidden|removed|redacted)\\s*\\]";

const hasContactRedactionArtifact = (value) =>
  new RegExp(CONTACT_REDACTION_ARTIFACT_SOURCE, "i").test(String(value ?? ""));

/**
 * Remove temporary redaction markers and surrounding contact-only copy.
 * This also cleans placeholders already inserted by an upstream API, so cards
 * never expose implementation text such as "[Business contact hidden]".
 */
const removeContactRedactionArtifacts = (value) =>
  String(value ?? "")
    .replace(
      new RegExp(
        `\\b${CONTACT_CUE_PATTERN}\\b(?:\\s+(?:me|us))?(?:\\s+(?:via|at))?\\s*[:\\-]?\\s*(?:${CONTACT_REDACTION_ARTIFACT_SOURCE})`,
        "gi"
      ),
      " "
    )
    .replace(
      new RegExp(
        `(${CONTACT_EMOJI_PATTERN})\\s*[:\\-]?\\s*(?:${CONTACT_REDACTION_ARTIFACT_SOURCE})`,
        "gi"
      ),
      "$1 "
    )
    .replace(new RegExp(CONTACT_REDACTION_ARTIFACT_SOURCE, "gi"), " ")
    .replace(
      /\b(?:e-?mail|contact|phone|mobile|telephone|whats\s*app)\s*:\s*(?=$|[|,;])/gi,
      " "
    )
    .replace(/\s+([,.;!?])/g, "$1")
    .replace(/\s*\|\s*(?=\||$)/g, " ")
    .replace(/\b(?:and|or)\s*$/i, "")
    .replace(/[|,;:/-]+\s*$/g, "")
    .replace(/\s{2,}/g, " ")
    .trim();

export function sanitizeCreatorTextWithMeta(value) {
  const original = String(value ?? "").trim();

  if (!original) {
    return {
      text: "",
      contactRedacted: false,
    };
  }

  const withoutEmailOrWhatsApp = emailOnlySanitize(original);
  const redacted = replacePhoneCandidates(withoutEmailOrWhatsApp);
  const contactRedacted =
    redacted !== original || hasContactRedactionArtifact(original);
  const sanitized = contactRedacted
    ? removeContactRedactionArtifacts(redacted)
    : original;

  return {
    text: sanitized,
    contactRedacted,
  };
}

export function sanitizeCreatorText(value) {
  return sanitizeCreatorTextWithMeta(value).text;
}

export function sanitizeCreatorUrl(value) {
  const raw = String(value ?? "").trim();
  if (!raw) return null;

  if (EMAIL_PATTERN.test(raw)) {
    EMAIL_PATTERN.lastIndex = 0;
    return null;
  }
  EMAIL_PATTERN.lastIndex = 0;

  if (RESTRICTED_CONTACT_URL_PATTERN.test(raw)) return null;
  return raw;
}

export function sanitizeCreatorBio(value) {
  return sanitizeCreatorTextWithMeta(value);
}


const unwrapCreatorText = (value) => {
  if (typeof value === "string") return value.trim();
  if (!value || typeof value !== "object" || Array.isArray(value)) return "";
  if (typeof value.S === "string") return value.S.trim();
  return "";
};

const readCreatorTextField = (source, key) => {
  if (!source || typeof source !== "object") return "";
  const container = source.M && typeof source.M === "object" ? source.M : source;
  return unwrapCreatorText(container?.[key]);
};

/**
 * Resolve biography text across discovery, saved-creator and details payloads,
 * then apply the same customer-facing cleanup to every response shape.
 */
export function extractCreatorBioPresentation(creator) {
  if (!creator || typeof creator !== "object") {
    return { text: "", contactRedacted: false };
  }

  const rawSource = creator.raw_source || creator.rawSource || {};
  const profile = creator.profile || {};
  const fields = [
    "bio",
    "biography",
    "description",
    "profile_summary",
    "profileSummary",
  ];
  let contactRedacted = false;

  for (const source of [creator, profile, rawSource]) {
    for (const field of fields) {
      const rawText = readCreatorTextField(source, field);
      if (!rawText) continue;

      const presentation = sanitizeCreatorBio(rawText);
      contactRedacted = contactRedacted || presentation.contactRedacted;

      if (presentation.text) {
        return { text: presentation.text, contactRedacted };
      }
    }
  }

  return { text: "", contactRedacted };
}

export function sanitizeCreatorRawSource(
  value,
  depth = 0,
  context = { humanText: false, url: false }
) {
  if (value === null || value === undefined) return value;
  if (depth > 12) return null;

  if (Array.isArray(value)) {
    return value
      .map((item) => sanitizeCreatorRawSource(item, depth + 1, context))
      .filter((item) => item !== null && item !== undefined);
  }

  if (typeof value !== "object") {
    if (typeof value !== "string") return value;
    if (context.url) return sanitizeCreatorUrl(value);
    return context.humanText ? sanitizeCreatorText(value) : value;
  }

  return Object.entries(value).reduce((output, [key, child]) => {
    if (SENSITIVE_CONTACT_KEY_PATTERN.test(key)) return output;

    const childContext = {
      humanText: context.humanText || HUMAN_TEXT_KEY_PATTERN.test(key),
      url: context.url || URL_KEY_PATTERN.test(key),
    };

    const sanitizedChild = sanitizeCreatorRawSource(
      child,
      depth + 1,
      childContext
    );

    if (sanitizedChild !== null && sanitizedChild !== undefined) {
      output[key] = sanitizedChild;
    }

    return output;
  }, {});
}

/**
 * Route state must be identification/prefetch metadata only. Do not copy the
 * complete API object (including raw_source, bio or contact details) into
 * browser history state.
 */
export function buildCreatorRouteSummary(influencer, routeId) {
  const id = String(
    routeId ||
      influencer?.influencerId ||
      influencer?.influencer_id ||
      influencer?.creatorId ||
      influencer?.creator_id ||
      influencer?.external_id ||
      influencer?.externalId ||
      influencer?.id ||
      ""
  ).trim();

  const username = String(
    influencer?.username || influencer?.handle || ""
  )
    .trim()
    .replace(/^@/, "");

  const profileImageUrl = String(
    influencer?.profileImageUrl ||
      influencer?.profile_image_url ||
      influencer?.profile_picture_url ||
      ""
  ).trim();

  const summary = {
    id: id || undefined,
    influencerId: id || undefined,
    creatorId: id || undefined,
    _routeId: id || undefined,
    username: username || undefined,
    handle: username || undefined,
    name:
      influencer?.name || influencer?.full_name || username || undefined,
    full_name: influencer?.full_name || undefined,
    platform: influencer?.platform || undefined,
    profileImageUrl: profileImageUrl || undefined,
    profile_image_url: profileImageUrl || undefined,
    dataSource: "summary",
    detailsLoaded: false,
    details_loaded: false,
  };

  Object.keys(summary).forEach(
    (key) => summary[key] === undefined && delete summary[key]
  );

  return summary;
}
